import React, { useMemo, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  engineerService,
  EngineerJob,
  Visit,
  getBrowserLocation,
} from '../../services/engineerService';
import { LOCKED_CP17_ENGINEER_TEMPLATE_ID } from '../../constants/lockedCertificates';

function formatDateTime(iso?: string | null) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

const outcomeOptions: Array<{ id: string; label: string; description: string }> = [
  {
    id: 'COMPLETED',
    label: 'Completed',
    description: 'All work done. Ready to invoice.',
  },
  {
    id: 'NEEDS_FOLLOWUP',
    label: 'Needs follow-up',
    description: 'Parts required or another visit needed.',
  },
  {
    id: 'COULDNT_COMPLETE',
    label: "Couldn't complete",
    description: 'Blocked on site. Office action required.',
  },
  {
    id: 'CUSTOMER_NOT_HOME',
    label: 'Customer not home',
    description: 'No answer at the property.',
  },
];

const EngineerJobDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showComplete, setShowComplete] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const [outcome, setOutcome] = useState<string>('COMPLETED');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [pauseReason, setPauseReason] = useState('PARTS_NEEDED');
  const [pauseNote, setPauseNote] = useState('');

  const { data: job, isLoading, error } = useQuery<EngineerJob>({
    queryKey: ['engineer', 'job', id],
    queryFn: () => engineerService.getJob(id!),
    enabled: !!id,
    refetchInterval: 60_000,
  });

  const activeVisit: Visit | undefined = useMemo(() => {
    return (job?.visits || []).find((v) => ['EN_ROUTE', 'ON_SITE', 'PAUSED'].includes(v.status));
  }, [job]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['engineer', 'job', id] });
    qc.invalidateQueries({ queryKey: ['engineer', 'today'] });
  };

  const startMut = useMutation({
    mutationFn: async () => {
      const location = await getBrowserLocation();
      return engineerService.startVisit(id!, location);
    },
    onSuccess: refresh,
  });

  const transitionMut = useMutation({
    mutationFn: async (args: {
      visitId: string;
      action: 'EN_ROUTE' | 'ARRIVED' | 'PAUSE' | 'RESUME' | 'COMPLETE' | 'CANCEL';
      extras?: any;
    }) => {
      const location = await getBrowserLocation();
      return engineerService.transition(args.visitId, args.action, {
        ...(args.extras || {}),
        location,
      });
    },
    onSuccess: (_, vars) => {
      refresh();
      if (vars.action === 'COMPLETE') setShowComplete(false);
      if (vars.action === 'PAUSE') setShowPause(false);
    },
  });

  if (isLoading) {
    return <div className="px-4 py-6 text-slate-500 text-sm">Loading…</div>;
  }
  if (error || !job) {
    return (
      <div className="px-4 py-6">
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          Couldn't load this job.
        </div>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm text-blue-600">
          Go back
        </button>
      </div>
    );
  }

  const telHref = job.client?.phone ? `tel:${job.client.phone.replace(/\s+/g, '')}` : null;
  const mapQuery = [job.address, job.postcode].filter(Boolean).join(', ');
  const mapHref = mapQuery ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapQuery)}` : null;

  return (
    <div className="max-w-xl mx-auto px-4 py-4 space-y-4">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500">← Back</button>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="text-xs font-medium text-slate-500">
          {job.scheduledDate ? formatDateTime(job.scheduledDate) : 'Unscheduled'}
        </div>
        <h1 className="mt-1 text-xl font-semibold text-slate-900">{job.title}</h1>
        {job.description && (
          <p className="mt-2 text-sm text-slate-700 whitespace-pre-line">{job.description}</p>
        )}
        {job.priority && (
          <div className="mt-3 inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full bg-slate-100 text-slate-700">
            {job.priority}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Customer</div>
        <div className="text-base font-semibold text-slate-900">{job.client?.name}</div>
        {[job.address, job.postcode].filter(Boolean).length > 0 && (
          <div className="mt-1 text-sm text-slate-600">
            {[job.address, job.postcode].filter(Boolean).join(', ')}
          </div>
        )}
        <div className="mt-3 flex gap-2">
          {telHref && (
            <a
              href={telHref}
              className="flex-1 text-center text-sm font-semibold bg-slate-900 text-white py-2.5 rounded-lg"
            >
              Call
            </a>
          )}
          {mapHref && (
            <a
              href={mapHref}
              target="_blank"
              rel="noreferrer"
              className="flex-1 text-center text-sm font-semibold border border-slate-300 text-slate-700 py-2.5 rounded-lg"
            >
              Navigate
            </a>
          )}
        </div>
      </div>

      {job.notes && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 mb-1">Notes</div>
          <div className="text-sm text-amber-900 whitespace-pre-line">{job.notes}</div>
        </div>
      )}

      <div className="sticky bottom-20 z-10">
        <ActionBar
          activeVisit={activeVisit}
          busy={startMut.isPending || transitionMut.isPending}
          onStart={() => startMut.mutate()}
          onArrived={() =>
            activeVisit && transitionMut.mutate({ visitId: activeVisit.id, action: 'ARRIVED' })
          }
          onPause={() => setShowPause(true)}
          onResume={() =>
            activeVisit && transitionMut.mutate({ visitId: activeVisit.id, action: 'RESUME' })
          }
          onComplete={() => setShowComplete(true)}
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Certificates
        </div>
        {(job.certificateKind === 'CP17_BREAKDOWN' || job.certificateTemplate) ? (
          <p className="text-xs text-slate-600 mb-2">
            Requested:{' '}
            <span className="font-medium text-slate-800">
              {job.certificateKind === 'CP17_BREAKDOWN'
                ? 'CP17 — Breakdown (locked)'
                : job.certificateTemplate?.templateLabel}
            </span>
          </p>
        ) : null}
        <button
          onClick={() =>
            navigate(
              job.certificateKind === 'CP17_BREAKDOWN'
                ? `/engineer/jobs/${job.id}/certificates/new/${LOCKED_CP17_ENGINEER_TEMPLATE_ID}`
                : job.certificateTemplateId
                  ? `/engineer/jobs/${job.id}/certificates/new/${job.certificateTemplateId}`
                  : `/engineer/jobs/${job.id}/certificates/new`,
            )
          }
          className="w-full py-3 rounded-lg border-2 border-dashed border-blue-300 text-blue-700 font-semibold hover:bg-blue-50/60"
        >
          + Complete a certificate
        </button>
      </div>

      {(job.visits?.length || 0) > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Visits</div>
          <ul className="space-y-2">
            {job.visits!.map((v) => (
              <li key={v.id} className="text-xs text-slate-600 flex items-center justify-between">
                <span>{v.status}</span>
                <span className="text-slate-400">{formatDateTime(v.endedAt || v.arrivedAt || v.enRouteAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showPause && activeVisit && (
        <Sheet onClose={() => setShowPause(false)} title="Pause this visit">
          <div className="space-y-3">
            <label className="block text-xs font-medium text-slate-600">Reason</label>
            <select
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
            >
              <option value="PARTS_NEEDED">Parts needed</option>
              <option value="WAITING_CUSTOMER">Waiting on customer</option>
              <option value="ACCESS_ISSUE">Access issue</option>
              <option value="BREAK">Break</option>
              <option value="OTHER">Other</option>
            </select>
            <textarea
              placeholder="Add a short note (optional)"
              value={pauseNote}
              onChange={(e) => setPauseNote(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              rows={3}
            />
            <button
              disabled={transitionMut.isPending}
              onClick={() =>
                transitionMut.mutate({
                  visitId: activeVisit.id,
                  action: 'PAUSE',
                  extras: { pauseReason, pauseNote: pauseNote || undefined },
                })
              }
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-lg"
            >
              Pause visit
            </button>
          </div>
        </Sheet>
      )}

      {showComplete && activeVisit && (
        <Sheet onClose={() => setShowComplete(false)} title="Complete this visit">
          <div className="space-y-3">
            <div className="space-y-2">
              {outcomeOptions.map((opt) => (
                <label
                  key={opt.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
                    outcome === opt.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="outcome"
                    checked={outcome === opt.id}
                    onChange={() => setOutcome(opt.id)}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
                    <div className="text-xs text-slate-500">{opt.description}</div>
                  </div>
                </label>
              ))}
            </div>
            <textarea
              placeholder="Notes for the office"
              value={outcomeNote}
              onChange={(e) => setOutcomeNote(e.target.value)}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              rows={3}
            />
            <button
              disabled={transitionMut.isPending}
              onClick={() =>
                transitionMut.mutate({
                  visitId: activeVisit.id,
                  action: 'COMPLETE',
                  extras: { outcome, outcomeNote: outcomeNote || undefined },
                })
              }
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg"
            >
              Submit
            </button>
          </div>
        </Sheet>
      )}
    </div>
  );
};

const ActionBar: React.FC<{
  activeVisit: Visit | undefined;
  busy: boolean;
  onStart: () => void;
  onArrived: () => void;
  onPause: () => void;
  onResume: () => void;
  onComplete: () => void;
}> = ({ activeVisit, busy, onStart, onArrived, onPause, onResume, onComplete }) => {
  const base = 'flex-1 text-white font-semibold py-3 rounded-lg disabled:opacity-60';

  if (!activeVisit) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg flex gap-2">
        <button disabled={busy} onClick={onStart} className={`${base} bg-blue-600 hover:bg-blue-700`}>
          On my way
        </button>
      </div>
    );
  }

  if (activeVisit.status === 'EN_ROUTE') {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg flex gap-2">
        <button disabled={busy} onClick={onArrived} className={`${base} bg-emerald-600 hover:bg-emerald-700`}>
          I'm here
        </button>
      </div>
    );
  }

  if (activeVisit.status === 'ON_SITE') {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg flex gap-2">
        <button disabled={busy} onClick={onPause} className={`${base} bg-amber-500 hover:bg-amber-600`}>
          Pause
        </button>
        <button disabled={busy} onClick={onComplete} className={`${base} bg-blue-600 hover:bg-blue-700`}>
          Complete
        </button>
      </div>
    );
  }

  if (activeVisit.status === 'PAUSED') {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-lg flex gap-2">
        <button disabled={busy} onClick={onResume} className={`${base} bg-emerald-600 hover:bg-emerald-700`}>
          Resume
        </button>
        <button disabled={busy} onClick={onComplete} className={`${base} bg-blue-600 hover:bg-blue-700`}>
          Complete
        </button>
      </div>
    );
  }

  return null;
};

const Sheet: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title,
  onClose,
  children,
}) => {
  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/40">
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="text-slate-400 text-2xl leading-none">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default EngineerJobDetail;
